import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, formatINR } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ReceiptText } from "lucide-react";

const empty = { name: "", default_rate: "", tax_percent: "", active: true };

export default function BillItems() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/bill-items").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditId(null); setForm(empty); setShow(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ name: it.name, default_rate: it.default_rate, tax_percent: it.tax_percent, active: it.active !== false }); setShow(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { name: form.name, default_rate: parseFloat(form.default_rate) || 0, tax_percent: parseFloat(form.tax_percent) || 0, active: form.active };
    try {
      if (editId) await api.put(`/bill-items/${editId}`, payload);
      else await api.post("/bill-items", payload);
      toast.success(editId ? "Item updated" : "Item added");
      setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this bill item?")) return;
    try { await api.delete(`/bill-items/${id}`); toast.success("Item deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (user && user.role !== "admin" && !(user.permissions || []).includes("bill-items")) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="bill-items-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><ReceiptText size={26} className="text-emerald-600" /> Bill Items</h1>
          <p className="text-sm text-slate-500 mt-1">Reusable line items available when Finance generates a bill for a lead.</p>
        </div>
        <button data-testid="add-bill-item-button" onClick={openNew} className={btnPrimary}><Plus size={16} /> New Item</button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[560px]">
          <thead><tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="text-left px-4 py-3">Item</th><th className="text-right px-4 py-3">Default Rate</th><th className="text-right px-4 py-3">Tax %</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`bill-item-row-${it.id}`} className="border-b border-slate-100 text-sm text-slate-700">
                <td className="px-4 py-3 font-medium text-slate-900">{it.name}</td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(it.default_rate)}</td>
                <td className="px-4 py-3 text-right">{it.tax_percent || 0}%</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${it.active !== false ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{it.active !== false ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-bill-item-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>
                  <button data-testid={`delete-bill-item-${it.id}`} onClick={() => del(it.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400 text-sm">No bill items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Edit Bill Item" : "New Bill Item"} testId="bill-item-modal">
        <form onSubmit={submit} className="space-y-4">
          <div><label className={labelCls}>Item Name *</label><input data-testid="bill-item-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Processing Fee" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Default Rate (₹)</label><input data-testid="bill-item-rate" type="number" value={form.default_rate} onChange={(e) => set("default_rate", e.target.value)} className={inputCls} placeholder="0" /></div>
            <div><label className={labelCls}>Tax %</label><input data-testid="bill-item-tax" type="number" value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} className={inputCls} placeholder="0" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="bill-item-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-emerald-600" /> Active</label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-bill-item-button" type="submit" className={btnPrimary}>{editId ? "Save" : "Add Item"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
